/**
 * PortCallTimelineChart - Line chart showing port call activity over time
 *
 * Shows the number of ongoing port calls (vessels in port) for each day.
 */
import { useMemo } from 'react';
import { Card, CardHeader, CardTitle } from '@ais-ui/ui-core';
import {
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Area,
  AreaChart,
} from 'recharts';
import { format } from 'date-fns';
import { PortCallData } from './types';
import { useLocalization } from '@ais-ui/locale-management';

interface PortCallTimelineChartProps {
  portCalls: PortCallData[];
  dateRange?: { from: Date; to: Date };
  height?: number;
}

interface DailyCount {
  date: string;
  dateObj: Date;
  count: number;
}

/**
 * Calculate the number of vessels in port for each day in the range
 */
const computeDailyOccupancy = (
  portCalls: PortCallData[],
  dateRange?: { from: Date; to: Date },
): DailyCount[] => {
  if (portCalls.length === 0) return [];

  // Determine date range
  let startDate: Date;
  let endDate: Date;

  if (dateRange?.from && dateRange?.to) {
    startDate = dateRange.from;
    endDate = dateRange.to;
  } else {
    // Use min/max from data
    const dates = portCalls.map((c) => c.ata.getTime());
    startDate = new Date(Math.min(...dates));
    endDate = new Date(Math.max(...dates));
  }

  // Generate all days in range
  const days: DailyCount[] = [];
  const currentDate = new Date(startDate);
  currentDate.setHours(0, 0, 0, 0);

  // oxlint-disable-next-line no-unmodified-loop-condition -- currentDate is advanced in place via setDate() at the end of the loop
  while (currentDate <= endDate) {
    const dayStart = new Date(currentDate);
    const dayEnd = new Date(currentDate);
    dayEnd.setHours(23, 59, 59, 999);

    // Count port calls that overlap with this day
    // A port call overlaps if: arrival <= dayEnd AND (departure >= dayStart OR no departure yet)
    const count = portCalls.filter((call) => {
      const arrival = call.ata;
      const departure = call.atd || new Date(); // If no departure, assume still in port

      return arrival <= dayEnd && departure >= dayStart;
    }).length;

    days.push({
      date: format(currentDate, 'MMM d'),
      dateObj: new Date(currentDate),
      count,
    });

    currentDate.setDate(currentDate.getDate() + 1);
  }

  return days;
};

export const PortCallTimelineChart = ({
  portCalls,
  dateRange,
  height = 200,
}: PortCallTimelineChartProps) => {
  const { messages } = useLocalization();
  const chartData = useMemo(() => {
    const range =
      dateRange?.from && dateRange?.to
        ? { from: dateRange.from, to: dateRange.to }
        : undefined;
    return computeDailyOccupancy(portCalls, range);
  }, [portCalls, dateRange]);

  const noDataMessage =
    messages.emptyStateNoData?.replace('{entity}', 'port call') ??
    'No port call data available';

  if (chartData.length === 0) {
    return (
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium">
            Vessels in Port Over Time
          </CardTitle>
        </CardHeader>
        <div
          className="px-6 pb-4 flex items-center justify-center text-muted-foreground"
          style={{ height }}
        >
          {noDataMessage}
        </div>
      </Card>
    );
  }

  const maxCount = Math.max(...chartData.map((d) => d.count));

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium">
          Vessels in Port Over Time
        </CardTitle>
      </CardHeader>
      <div className="px-6 pb-4">
        <ResponsiveContainer width="100%" height={height}>
          <AreaChart
            data={chartData}
            margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
          >
            <defs>
              <linearGradient id="colorCount" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#3B82F6" stopOpacity={0.3} />
                <stop offset="95%" stopColor="#3B82F6" stopOpacity={0} />
              </linearGradient>
            </defs>
            <XAxis
              dataKey="date"
              tick={{ fontSize: 11 }}
              tickLine={false}
              axisLine={{ stroke: 'hsl(var(--border))' }}
              interval="preserveStartEnd"
            />
            <YAxis
              allowDecimals={false}
              tick={{ fontSize: 11 }}
              tickLine={false}
              axisLine={{ stroke: 'hsl(var(--border))' }}
              domain={[0, Math.max(maxCount + 1, 5)]}
            />
            <Tooltip
              formatter={(value: number) => [value, 'Vessels']}
              labelFormatter={(label) => `Date: ${label}`}
              contentStyle={{
                backgroundColor: 'hsl(var(--background))',
                border: '1px solid hsl(var(--border))',
                borderRadius: '6px',
                fontSize: '12px',
              }}
            />
            <Area
              type="monotone"
              dataKey="count"
              stroke="#3B82F6"
              strokeWidth={2}
              fillOpacity={1}
              fill="url(#colorCount)"
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </Card>
  );
};
